import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  filterCatalog,
  getPriceBounds,
  type StorefrontSearch,
} from "@/lib/storefront/catalog-filters";
import { storefrontTagsQueryOptions } from "@/lib/storefront/catalog";
import { useStorefrontCatalog } from "./use-storefront-catalog";

const PAGE_SIZE = 24;

export interface CatalogFacet {
  id: string;
  name: string;
  count: number;
}

/**
 * Everything the catalogue page renders for a search: the matching products
 * (a page at a time), the categories and tags to filter by with their product
 * counts, and the price range of the whole catalogue.
 */
export function useCatalogView(search: StorefrontSearch) {
  const { data: categories } = useStorefrontCatalog();
  const { data: tags = [] } = useQuery(storefrontTagsQueryOptions());

  const allProducts = useMemo(
    () => categories.flatMap((category) => category.products),
    [categories]
  );

  const products = useMemo(
    () => filterCatalog(categories, search),
    [categories, search]
  );

  const categoryFacets = useMemo<CatalogFacet[]>(
    () =>
      [...categories]
        .sort((a, b) => a.position - b.position)
        .map((category) => ({
          id: category.id,
          name: category.name,
          count: category.products.length,
        })),
    [categories]
  );

  // Only tags a listed product carries; counted over the whole catalogue so
  // the numbers do not jump while filtering.
  const tagFacets = useMemo<CatalogFacet[]>(() => {
    const counts = new Map<string, number>();
    allProducts.forEach((product) =>
      product.tagIds.forEach((tagId) =>
        counts.set(tagId, (counts.get(tagId) ?? 0) + 1)
      )
    );
    return tags
      .filter((tag) => counts.has(tag.id))
      .map((tag) => ({ id: tag.id, name: tag.name, count: counts.get(tag.id)! }));
  }, [allProducts, tags]);

  const priceBounds = useMemo(() => getPriceBounds(allProducts), [allProducts]);

  // A new search starts back at the first page.
  const searchKey = JSON.stringify(search);
  const [paging, setPaging] = useState({ key: searchKey, count: PAGE_SIZE });
  const visibleCount = paging.key === searchKey ? paging.count : PAGE_SIZE;

  return {
    products: products.slice(0, visibleCount),
    totalCount: products.length,
    hasMore: products.length > visibleCount,
    showMore: () =>
      setPaging({ key: searchKey, count: visibleCount + PAGE_SIZE }),
    categoryFacets,
    tagFacets,
    priceBounds,
    activeCategory: categoryFacets.find(
      (category) => category.id === search.category
    ),
  };
}
